import { useCallback, useEffect, useState, useMemo } from 'react'
import debounce from 'lodash/debounce'

import breakpoints from '@/checkout-instore-legacy/constants/breakpoints'
import {
  mediaQuery,
  getCurrentBreakpoint,
} from '@/checkout-instore-legacy/utils/mediaQuery'

type BreakpointType = keyof typeof breakpoints

export function useOnWindowResize(
  onBreakpointChange: (breakpoint: BreakpointType) => void
) {
  const handleWindowResize = useCallback(
    debounce(() => {
      onBreakpointChange(getCurrentBreakpoint())
    }, 100),
    []
  )

  useEffect(
    function windowResized() {
      window.addEventListener('resize', handleWindowResize)
      return () => {
        window.removeEventListener('resize', handleWindowResize)
      }
    },
    [handleWindowResize]
  )
}

type UseMediaQueryReturnType<T> = {
  value: T
  breakpoint: BreakpointType
}

export function useMediaQuery<T = number | boolean | string>(
  values: Record<string, T>
): UseMediaQueryReturnType<T> {
  const [breakpoint, setBreakpoint] = useState<BreakpointType>(
    getCurrentBreakpoint()
  )

  useOnWindowResize(setBreakpoint)

  const value = useMemo(() => mediaQuery(values), [values])

  return {
    value,
    breakpoint,
  }
}
